import {
  formatMoney,
  OUTGOING_PAYMENT_METHOD_LABELS,
  type OutgoingPayment,
  queryKeys,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  humanizeStatus,
} from '@mevaser/ui'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import { RecordPaymentDialog, usePaymentLookups } from './record-payment-dialog.js'

const TONE = { unpaid: 'warning', paid: 'success', void: 'neutral' } as const

/**
 * Payments made to a vendor, or against one purchase order (t1164), with a
 * button to record another. Renders nothing for staff without `expenses:read`.
 * A purchase order knows its vendor only by name, so `vendorName` is matched
 * against the vendor directory to pre-fill the dialog.
 */
export function PaymentsCard({
  vendorId,
  vendorName,
  purchaseOrderId,
}: {
  vendorId?: string
  vendorName?: string
  purchaseOrderId?: string
}) {
  const { can } = useAuth()
  const allowed = can('expenses:read')
  const [recording, setRecording] = useState(false)
  const lookups = usePaymentLookups(allowed && !vendorId && Boolean(vendorName))
  const resolvedVendorId =
    vendorId ??
    lookups.vendors.find((v) => v.name.trim().toLowerCase() === vendorName?.trim().toLowerCase())
      ?.id

  const filters = { vendorId, purchaseOrderId, pageSize: 10 }
  const { data } = useQuery({
    queryKey: queryKeys.expenses.payments(filters),
    queryFn: () =>
      api.get<{ items: OutgoingPayment[]; total: number }>('/outgoing-payments', {
        query: filters,
      }),
    enabled: allowed,
  })

  // t1223: office supplies entered with this order's invoice are expenses, not payments toward it.
  const supplyFilters = { invoicePurchaseOrderId: purchaseOrderId, pageSize: 10 }
  const { data: supplies } = useQuery({
    queryKey: queryKeys.expenses.payments(supplyFilters),
    queryFn: () =>
      api.get<{ items: OutgoingPayment[]; total: number }>('/outgoing-payments', {
        query: supplyFilters,
      }),
    enabled: allowed && Boolean(purchaseOrderId),
  })

  const prefill = useMemo(
    () => ({ kind: 'vendor' as const, vendorId: resolvedVendorId, purchaseOrderId }),
    [resolvedVendorId, purchaseOrderId],
  )

  if (!allowed) return null
  const items = data?.items ?? []

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle>Payments</CardTitle>
        {can('expenses:manage') ? (
          <Button variant="secondary" size="sm" onClick={() => setRecording(true)}>
            <Plus /> Record payment
          </Button>
        ) : null}
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-foreground-muted">No payments recorded yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((p) => (
              <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                <div>
                  <span className="font-medium">{p.number}</span>{' '}
                  <span className="text-foreground-muted">
                    {p.paidOn ?? (p.dueOn ? `due ${p.dueOn}` : '')}
                    {p.method ? ` · ${OUTGOING_PAYMENT_METHOD_LABELS[p.method]}` : ''}
                    {p.purchaseOrderNumber && !purchaseOrderId ? ` · ${p.purchaseOrderNumber}` : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="tabular font-medium">{formatMoney(p.amountCents)}</span>
                  <Badge tone={TONE[p.status]}>{humanizeStatus(p.status)}</Badge>
                </div>
              </li>
            ))}
          </ul>
        )}
        {(supplies?.items ?? []).length > 0 ? (
          <div className="mt-3 border-t border-border pt-3">
            <p className="text-xs font-medium text-foreground-muted">
              Office supplies on this invoice
            </p>
            <ul className="divide-y divide-border">
              {(supplies?.items ?? []).map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                  <div>
                    <span className="font-medium">{p.number}</span>{' '}
                    <span className="text-foreground-muted">
                      {p.categoryName ?? 'Expense'}
                      {p.paidOn ? ` · paid ${p.paidOn}` : ''}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="tabular font-medium">{formatMoney(p.amountCents)}</span>
                    <Badge tone={TONE[p.status]}>{humanizeStatus(p.status)}</Badge>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {(data?.total ?? 0) > items.length ? (
          <p className="mt-2 text-xs text-foreground-muted">
            Showing the latest {items.length} of {data?.total}.{' '}
            <Link to="/payments" className="text-primary hover:underline">
              Open Payments & expenses
            </Link>
          </p>
        ) : null}
      </CardContent>
      <RecordPaymentDialog open={recording} onClose={() => setRecording(false)} prefill={prefill} />
    </Card>
  )
}
